// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the briefing pack (Situation room, slice CR3): one printable page
 * per briefing, for the file and for whoever was not in the room. Each step
 * shown, in order, with a picture of every globe as it was, the stream, the
 * frame's date and how old the data was then; the decisions; the credits
 * the data's licences ask for; and the record's SHA-256, so a printed pack
 * can be matched to the saved record. Built in the browser and printed (or
 * saved as PDF) from a new tab, as a lesson's worksheet is; nothing is sent
 * anywhere.
 */

import { getLocale, t } from '../i18n'
import { escapeHtml } from './domUtils'
import type { BriefingDecision, BriefingStepShown } from '../services/briefingRecord'
import type { FreshnessLevel } from '../services/streamFreshness'

export interface BriefingPackInput {
  title: string
  startedAt: number
  endedAt: number
  /** The briefer, when the record carries their name. */
  who: string | null
  steps: ReadonlyArray<BriefingStepShown & { pictures: ReadonlyArray<string | null> }>
  decisions: readonly BriefingDecision[]
  credits: readonly string[]
  /** The sealed record's content hash, when it was sealed. */
  contentSha256: string | null
  lang?: string
}

const FRESHNESS_KEY: Record<FreshnessLevel, 'situation.fresh.fresh' | 'situation.fresh.late' | 'situation.fresh.stale' | 'situation.fresh.unknown'> = {
  fresh: 'situation.fresh.fresh',
  late: 'situation.fresh.late',
  stale: 'situation.fresh.stale',
  unknown: 'situation.fresh.unknown',
}

/** A date as the pack prints it: UTC, to the minute. */
function utc(ms: number): string {
  return `${new Date(ms).toISOString().slice(0, 16).replace('T', ' ')} UTC`
}

/** Only pictures this page made: a JPEG or PNG data URL, never a link. */
const isPicture = (src: string | null): src is string => typeof src === 'string' && /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(src)

export function briefingPackHtml(input: BriefingPackInput): string {
  const lang = input.lang ?? getLocale()
  const steps = input.steps.map((s, i) => {
    const decision = input.decisions.find((d) => d.stepId === s.stepId)
    const lastShowing = !input.steps.slice(i + 1).some((x) => x.stepId === s.stepId)
    const globes = s.globes.map((g, gi) => {
      const src = s.pictures[gi] ?? null
      const when = g.frameMs === null ? '' : utc(g.frameMs)
      const age = g.freshness ? escapeHtml(t(FRESHNESS_KEY[g.freshness])) : ''
      return `<figure class="globe">
        ${isPicture(src) ? `<img src="${src}" alt="${escapeHtml(g.title)}">` : `<div class="nopic">${escapeHtml(t('situation.pack.noPicture'))}</div>`}
        <figcaption><strong>${escapeHtml(g.title)}</strong>${when ? `<br>${escapeHtml(when)}` : ''}${age ? ` · <span class="age age-${escapeHtml(g.freshness ?? '')}">${age}</span>` : ''}</figcaption>
      </figure>`
    }).join('')
    return `<section class="step">
      <h2>${i + 1}. ${escapeHtml(s.title)} <span class="t">${escapeHtml(utc(input.startedAt + s.t))}</span></h2>
      <div class="globes n${Math.min(s.globes.length, 4)}">${globes}</div>
      ${decision && lastShowing ? `<div class="decision"><p><strong>${escapeHtml(t('situation.pack.decision'))}</strong></p><p>${escapeHtml(decision.text).replace(/\n/g, '<br>')}</p></div>` : ''}
    </section>`
  }).join('')
  const decisions = input.decisions.length
    ? `<section class="decisions"><h2>${escapeHtml(t('situation.pack.decisions'))}</h2><ol>${input.decisions.map((d) => `<li><span class="t">${escapeHtml(utc(input.startedAt + d.t))}</span> ${escapeHtml(d.text).replace(/\n/g, '<br>')}</li>`).join('')}</ol></section>`
    : `<p class="meta">${escapeHtml(t('situation.pack.noDecision'))}</p>`
  return `<!doctype html>
<html lang="${escapeHtml(lang)}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(input.title)}</title>
<style>
  body { font: 11pt/1.45 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #111; background: #fff; margin: 0 auto; max-width: 52rem; padding: 1.5rem; }
  h1 { font-size: 17pt; margin: 0 0 0.2rem; }
  h2 { font-size: 12.5pt; margin: 1.1rem 0 0.4rem; }
  .meta, .t, figcaption, .notice { color: #444; font-size: 9.5pt; }
  .t { font-weight: 400; }
  .globes { display: grid; gap: 0.5rem; grid-template-columns: 1fr; }
  .globes.n2, .globes.n3, .globes.n4 { grid-template-columns: 1fr 1fr; }
  .globe { margin: 0; }
  .globe img { inline-size: 100%; block-size: auto; display: block; border-radius: 4px; background: #000; }
  .nopic { aspect-ratio: 16 / 9; display: grid; place-items: center; background: #eee; color: #555; border-radius: 4px; font-size: 9.5pt; }
  .age-late { color: #8a5a00; font-weight: 600; }
  .age-stale { color: #a11; font-weight: 600; }
  .decision, .decisions { border-inline-start: 3px solid #1565c0; padding-inline-start: 0.6rem; }
  .decision p { margin: 0.2rem 0; }
  .step { break-inside: avoid; }
  .notice { margin-top: 1.5rem; border-top: 1px solid #ccc; padding-top: 0.5rem; word-break: break-all; }
  @media print { body { padding: 0; } @page { margin: 1.4cm; } }
</style>
</head>
<body>
  <h1>${escapeHtml(input.title)}</h1>
  <p class="meta">${escapeHtml(t('situation.pack.when', { from: utc(input.startedAt), to: utc(input.endedAt) }))}${input.who ? ` · ${escapeHtml(t('situation.pack.by', { name: input.who }))}` : ''}</p>
  ${decisions}
  ${steps}
  <p class="notice">${input.credits.map((c) => escapeHtml(c)).join('<br>')}${input.contentSha256 ? `${input.credits.length ? '<br>' : ''}${escapeHtml(t('situation.pack.hash', { hash: input.contentSha256 }))}` : ''}</p>
</body>
</html>`
}

/** Open the pack in a new tab and ask to print it there (or save it as PDF); a blocked tab saves it as a file. */
export function printBriefingPack(input: BriefingPackInput): void {
  const blob = new Blob([briefingPackHtml(input)], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const tab = window.open(url, '_blank')
  if (tab) {
    tab.addEventListener('load', () => {
      try {
        tab.focus()
        tab.print()
      } catch {
        /* the tab is open; printing is the reader's */
      }
    }, { once: true })
  } else {
    const a = document.createElement('a')
    a.href = url
    a.download = `ayni-briefing-${new Date(input.startedAt).toISOString().slice(0, 16).replace(/[:T]/g, '-')}.html`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
